import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { HERO } from "../../data/content";
import { LineReveal } from "../motion/Reveal";
import { Sprig } from "../motion/Sprig";

const ease = [0.22, 1, 0.36, 1];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.05, 1.25]);
  const overlay = useTransform(scrollYProgress, [0, 1], [0.35, 0.75]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative isolate min-h-[80vh] sm:min-h-screen w-full overflow-hidden flex flex-col justify-end"
    >
      {/* Parallax background */}
      <motion.div style={{ y, scale }} className="absolute inset-0 z-0">
        <img
          src={HERO.bg}
          alt="Misty hemlock forest"
          className="h-full w-full object-cover"
        />
      </motion.div>
      <motion.div
        style={{ opacity: overlay }}
        className="absolute inset-0 z-0 bg-black"
      />
      <div className="absolute inset-0 z-0 bg-gradient-to-t from-[var(--hem-bg)] via-transparent to-black/20" />

      {/* Floating sprig */}
      <div className="pointer-events-none absolute right-[6%] top-[18%] z-10 hidden lg:block text-[var(--hem-needle)]/25 hem-float">
        <Sprig className="h-72 w-24" strokeWidth={0.8} />
      </div>

      <div className="relative z-10 mx-auto w-full max-w-[1500px] px-6 md:px-12 pb-16 sm:pb-20 md:pb-28">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.3 }}
          className="mb-6 md:mb-8 flex items-center gap-3 md:gap-4"
        >
          <span className="h-px w-8 md:w-12 bg-[var(--hem-needle)]/50 shrink-0" />
          <span className="font-mono-tech text-[9px] sm:text-[11px] uppercase tracking-[0.18em] sm:tracking-[0.35em] text-[var(--hem-needle)]">
            {HERO.eyebrow}
          </span>
        </motion.div>

        <h1
          data-testid="hero-heading"
          className="font-serif-display font-light leading-[0.95] sm:leading-[0.92] tracking-tight text-[14vw] sm:text-[11vw] lg:text-[8.2vw]"
        >
          <LineReveal lines={HERO.lines} delay={0.5} />
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease, delay: 1.2 }}
          className="mt-8 md:mt-10 max-w-md font-mono-tech text-[13px] sm:text-sm leading-relaxed text-[var(--hem-muted)]"
        >
          {HERO.sub}
        </motion.p>
      </div>
    </section>
  );
};
